import { useState } from 'react';
import { Link } from 'react-router-dom';
import QuickBooksSyncDialog from './QuickBooksSyncDialog.jsx';
import {
  INTEGRATION_CATALOG,
  INTEGRATION_PRICING,
  formatIntegrationPrice,
  integrationPricingForConnections,
} from '../../lib/settingsStore.js';
import { formatMatterMoney } from '../../lib/matterCurrency.js';

const REQUEST_INTEGRATION_MAILTO =
  'mailto:support@demandjetapp.com?subject=' +
  encodeURIComponent('Request a new DemandJet accounting integration') +
  '&body=' +
  encodeURIComponent(
    [
      'Hello DemandJet support,',
      '',
      'I would like to request a new accounting software integration.',
      '',
      'Accounting software name:',
      'Company / organization:',
      'Why we need this integration:',
      '',
      'Thank you.',
    ].join('\n')
  );

export default function IntegrationsPanel({
  connections,
  connectProvider,
  connectMessage,
  onOpenConnect,
  onContinueOAuth,
  onDemoConnect,
  onCancelConnect,
  onDisconnect,
  onSyncQuickBooks,
  syncResult,
  syncNotice,
  syncing = false,
  syncProgress,
  syncChoice,
  autoSyncText,
  pending,
}) {
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  const providerMeta = connectProvider
    ? INTEGRATION_CATALOG.find((p) => p.id === connectProvider)
    : null;
  const pricing = integrationPricingForConnections(connections);
  const unitPrice = formatIntegrationPrice(INTEGRATION_PRICING.connectionMonthly);
  const connectPrice = providerMeta ? pricing.byId[providerMeta.id] : null;

  return (
    <div className="settings-form">
      <h2>Integrations</h2>
      <p className="settings-lead">
        Connect accounting platforms to import invoices and write back paid status after collection.
        Each connection is a monthly add-on at <strong>{unitPrice}/mo</strong>.
      </p>

      <div className="integration-pricing-card">
        <div>
          <h3>Integration add-on</h3>
          <p>
            Accounting connectors are billed at a flat <strong>{unitPrice}/mo</strong> per active
            connection. Demands and archive are not metered.
          </p>
        </div>
        <div className="integration-pricing-summary" aria-label="Current integration charges">
          <span className="integration-pricing-amount">
            {formatIntegrationPrice(pricing.monthlyTotal)}
            <small>/ mo</small>
          </span>
          <span className="integration-pricing-meta">
            {pricing.connectedCount === 0
              ? 'No active connectors'
              : `${pricing.connectedCount} active connector${
                  pricing.connectedCount === 1 ? '' : 's'
                } · ${unitPrice} each`}
          </span>
        </div>
      </div>

      <h3 className="settings-inline-title">Connected apps</h3>
      <p className="settings-sublead">
        When you connect an app, DemandJet sends you to that provider to sign in and grant access.
        Supported apps: QuickBooks, Zoho Books, Xero, Sage, Oracle NetSuite, FreshBooks, and Wave.
      </p>

      <div className="connection-list">
        {connections.map((conn) => {
          const connected = conn.status === 'connected';
          const isQuickBooks = conn.id === 'QUICKBOOKS';
          const price = pricing.byId[conn.id];
          return (
            <article key={conn.id} className="connection-row">
              <div>
                <h3>
                  {conn.name}
                  {conn.demo ? <span className="demo-pill">Demo</span> : null}
                  {connected && conn.primary ? (
                    <span className="demo-pill">Primary</span>
                  ) : null}
                </h3>
                <p>{conn.description}</p>
                {price ? (
                  <p className="integration-price-line">
                    {connected ? 'Billed at' : 'Connect for'} <strong>{price.label}</strong>
                    {' · connection add-on'}
                  </p>
                ) : null}
                {connected && conn.connectedAt ? (
                  <small className="field-hint connection-meta">
                    Connected {new Date(conn.connectedAt).toLocaleString()}
                    {conn.realmId ? ` · Company ${conn.realmId}` : ''}
                    {conn.lastSyncAt
                      ? ` · Last sync ${new Date(conn.lastSyncAt).toLocaleString()}`
                      : ''}
                  </small>
                ) : null}
                {isQuickBooks && connected && !conn.demo && autoSyncText ? (
                  <small className="field-hint connection-meta">{autoSyncText}</small>
                ) : null}
                {isQuickBooks && connected && syncing ? (
                  <div className="sync-progress" role="status" aria-live="polite">
                    <div className="sync-progress-text">
                      {syncProgress?.total
                        ? `Importing invoices… ${syncProgress.processed} of ${syncProgress.total} checked · ${syncProgress.imported} imported`
                        : 'Checking QuickBooks for invoices… this can take a few seconds.'}
                    </div>
                    <div
                      className={`sync-progress-bar${syncProgress?.total ? '' : ' is-indeterminate'}`}
                    >
                      <span
                        style={
                          syncProgress?.total
                            ? {
                                width: `${Math.round(
                                  (syncProgress.processed / syncProgress.total) * 100
                                )}%`,
                              }
                            : undefined
                        }
                      />
                    </div>
                    <small className="field-hint">
                      You can leave this page. The sync keeps running in the background.
                    </small>
                  </div>
                ) : null}
                {isQuickBooks && connected && syncNotice ? (
                  <p
                    className={`sync-notice ${syncNotice.tone === 'error' ? 'is-error' : 'is-success'}`}
                    role="status"
                    aria-live="polite"
                  >
                    {syncNotice.text}
                    {syncNotice.tone === 'success' && syncNotice.imported > 0 ? (
                      <>
                        {' '}
                        <Link className="text-link strong" to="/client/demands">
                          View demands →
                        </Link>
                      </>
                    ) : null}
                  </p>
                ) : null}
              </div>
              <div className="connection-actions">
                <span className={connected ? 'conn-badge connected' : 'conn-badge'}>
                  {connected ? 'Connected' : 'Not connected'}
                </span>
                {connected ? (
                  <>
                    {isQuickBooks ? (
                      <button
                        type="button"
                        className={`btn-primary conn-connect${syncing ? ' is-busy' : ''}`}
                        onClick={() => setSyncDialogOpen(true)}
                        disabled={pending || syncing}
                        aria-busy={syncing}
                      >
                        {syncing ? (
                          <>
                            <span className="btn-spinner" aria-hidden="true" />
                            Syncing…
                          </>
                        ) : (
                          'Sync invoices'
                        )}
                      </button>
                    ) : null}
                    <button
                      type="button"
                      className="btn-ghost"
                      onClick={() => onDisconnect(conn.id)}
                      disabled={pending || syncing}
                    >
                      Disconnect
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    className="btn-primary conn-connect"
                    onClick={() => onOpenConnect(conn.id)}
                    disabled={pending}
                  >
                    Connect {conn.name}
                  </button>
                )}
              </div>
            </article>
          );
        })}

        <article className="connection-row connection-row-request">
          <div>
            <h3>Request a new integration</h3>
            <p>
              Don’t see your accounting software? Tell us which platform you need and we’ll review
              it for a future release. Approved connectors use the same monthly pricing.
            </p>
          </div>
          <div className="connection-actions">
            <a className="btn-primary conn-connect" href={REQUEST_INTEGRATION_MAILTO}>
              Request integration
            </a>
          </div>
        </article>
      </div>

      {syncResult ? (
        <div className="connect-box">
          <h3>Last QuickBooks sync</h3>
          <p>
            Imported <strong>{syncResult.imported}</strong> open invoice
            {syncResult.imported === 1 ? '' : 's'}
            {syncResult.skipped ? ` · skipped ${syncResult.skipped}` : ''}
            {syncResult.mock ? ' · demo data' : ''}.
          </p>
          {syncResult.matters?.length ? (
            <ul className="sync-matter-list">
              {syncResult.matters.map((m) => (
                <li key={m.ROWID}>
                  <strong>{m.Debtor_Name}</strong>
                  <span>
                    {m.Debtor_State} · {formatMatterMoney(m.Amount_Owed, m)} ·{' '}
                    {m.External_Invoice_ID}
                  </span>
                </li>
              ))}
            </ul>
          ) : null}
          <p style={{ marginTop: '0.75rem' }}>
            <Link className="text-link strong" to="/client/demands">
              Open workspace to send demands from QuickBooks →
            </Link>
          </p>
        </div>
      ) : null}

      {connections.some((c) => c.id === 'QUICKBOOKS' && c.status === 'connected') ? (
        <div className="connect-box">
          <h3>Send demands from QuickBooks</h3>
          <p>
            Choose open invoices in your company file and send them into the DemandJet attorney
            pipeline as automated demands.
          </p>
          <Link className="btn-primary conn-connect" to="/client/demands">
            Go to Send from QuickBooks
          </Link>
        </div>
      ) : null}

      {providerMeta ? (
        <div className="social-modal-backdrop" role="presentation" onClick={onCancelConnect}>
          <div
            className="social-modal connect-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="connect-provider-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="social-modal-head">
              <h2 id="connect-provider-title">Connect {providerMeta.name}</h2>
              <button
                type="button"
                className="social-modal-close"
                onClick={onCancelConnect}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <p className="social-modal-hint">
              You’ll be sent to {providerMeta.name} to sign in and grant DemandJet permission to
              import invoices and update payment status.
            </p>
            {connectPrice ? (
              <p className="integration-connect-price">
                This connection bills at <strong>{connectPrice.label}</strong> as an integration
                add-on.
              </p>
            ) : null}
            {connectMessage ? <p className="settings-sublead">{connectMessage}</p> : null}
            <div className="connect-modal-actions">
              <button
                type="button"
                className="btn-primary social-modal-create"
                onClick={onContinueOAuth}
                disabled={pending}
              >
                {pending ? 'Opening…' : `Continue to ${providerMeta.name}`}
              </button>
              <button
                type="button"
                className="btn-ghost"
                onClick={onDemoConnect}
                disabled={pending}
              >
                Use demo connection
              </button>
              <button type="button" className="btn-ghost" onClick={onCancelConnect}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {syncDialogOpen ? (
        <QuickBooksSyncDialog
          initialChoice={syncChoice}
          onCancel={() => setSyncDialogOpen(false)}
          onSync={(choice) => {
            setSyncDialogOpen(false);
            onSyncQuickBooks(choice);
          }}
        />
      ) : null}
    </div>
  );
}
